import React, { useCallback, useMemo, useEffect, useState } from 'react';
import css from './styles.module.scss';
import { withStyles, Theme, createStyles } from '@material-ui/core/styles';
import {
    Grid,
    TextField,
    Button,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    Paper,
    CircularProgress,
    Box,
    Avatar,
} from '@material-ui/core';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { useRouteMatch } from 'react-router-dom';
import { getDealsThunk } from '~/features/dashboard/store/actions.thunks';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { notifySuccess } from '~/common/notifications';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import classNames from 'classnames';
import { VerticalPopver } from '~/features/dashboard/components/vertical-popover';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import { DateFormat } from '~/common/date-format';
import { axiosInstance } from '~/api/config/axios.config';
import { useSelector } from 'react-redux';
import { selectDeals } from '~/features/dashboard/store/selectors';
import { Pagination } from '@material-ui/lab';
import { usePagination } from '~/utils/hooks/usePagination';
import { HighlightOffOutlined, CheckCircleOutlineRounded, CloudUploadOutlined, CloudDoneOutlined, GetApp } from '@material-ui/icons';

export interface GetInvoices_props {
    id: number;
    name: string;
    dealID: string;
    startDate: string;
    endDate: string;
    amount: string;
    currency: string;
    isPaid: any;
    paymentDate: string;
    paymentMethod: any;
    paymentFilePath: string;
    paymentReceivedDate: string;
    loading: any;
    periodItCovers: string;
}

type Inputs = {
    example: string;
    exampleRequired: string;
};

const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
        head: {
            backgroundColor: 'transparent',
            color: theme.palette.common.black,
        },
        body: {
            fontSize: 14,
        },
    })
)(TableCell);

const ITEMS_PER_PAGE = 10;

export const MoneyCardEdit = () => {
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useThunkDispatch();
    const match = useRouteMatch<{ creatorId: string; dealId: string }>();
    const deals = useSelector(selectDeals);
    const [dealTitle, setDealTitle] = useState('');
    const [tableData, setTableData] = useState<GetInvoices_props[]>([]);
    const [isEdiTable, setIsEditTable] = useState(false);
    const { register, handleSubmit, setValue } = useForm<Inputs>();
    const [uploadingProgress, setUploadingProgress] = useState<{ id: number; loading: boolean }[]>([]);  // eslint-disable-line

    const { total, setTotal, setCurrentPage, currentPage, pagesCount } = usePagination(ITEMS_PER_PAGE);

    const [editRowData, setEditRowData] = useState<GetInvoices_props>({
        id: 0,
        name: '',
        dealID: '',
        startDate: '',
        endDate: '',
        amount: '',
        currency: '',
        isPaid: '',
        paymentDate: '',
        paymentMethod: '',
        paymentFilePath: '',
        loading: false,
        paymentReceivedDate: 'ssj',
        periodItCovers: '',
    });
    const route = useAppRoute();

    useEffect(() => {
        if (match.params.creatorId) {
            dispatch(getDealsThunk({ creatorId: Number(match.params.creatorId) }));
        }
    }, [dispatch, match.params.creatorId]);

    useEffect(() => {
        const currentDeal = deals.find(d => d.assetCode === match.params.dealId);
        if (currentDeal) {
            setDealTitle(currentDeal.title);
            setIsLoading(false);
        }
    }, [deals, match.params.dealId]);

    const fetchInvoices = async (dealId: string, pageSize: number, pageNumber: number) => {
        // TODO: use Thunk
        await axiosInstance
            .get('/Catalogue/projectcreator/invoicelist', {
                params: {
                    dealId,
                    pageNumber,
                    pageSize,
                },
            })
            .then(({ data }) => {
                if (data && data.length > 0) {
                    if (data[0].isDateNotKnown === 1) {
                        return [];
                    }
                    let tempData = data;
                    // setTotalCount(data[0]?.totalRows);
                    setTotal(data[0]?.totalRows);

                    tempData = tempData.map(function (el: any) {
                        var o = Object.assign({}, el);
                        o.loading = false;
                        return o;
                    });
                    setTableData(tempData);
                    setIsLoading(false);
                }
            });
    };

    useEffect(() => {
        fetchInvoices(match.params.dealId, ITEMS_PER_PAGE, currentPage);
    }, [currentPage]); // eslint-disable-line

    const editTableData = async (a: number) => {
        const selectedRow = tableData && tableData.find(row => row.id === a);
        if (selectedRow) {
            setEditRowData(selectedRow);
        }
        setIsEditTable(true);
    };

    const deleteTableData = async (id: number) => {
        const filteredData = tableData.filter(row => row.id !== id);
        await axiosInstance.delete(`/catalogue/projectCreator/deleteInvoiceById/${id}`);
        setTableData(filteredData);
    };

    const onSubmit = async (data: any) => {
        const currentTable = [...tableData];
        data.id = Number(data.id);
        data.paymentMethod = Number(data.paymentMethod);
        data.isPaid = Number(data.isPaid);
        if (data.paymentFilePath === '') {
            delete data.paymentFilePath;
        }
        const index = currentTable.findIndex(item => item.id === data.id);
        currentTable[index] = data;
        setTableData(currentTable);
        setIsEditTable(false);
        await axiosInstance
            .put('catalogue/projectCreator/updateInvoice', {
                ...currentTable[index],
            })
            .then(() => {
                notifySuccess({
                    title: 'Success',
                    message: `Invoice Updated Successfully`,
                });
            });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const files = e.target.files;
        const formData = new FormData();
        if (files) {
            formData.append('file', files[0]);
            const tempTable = [...tableData];
            const index = tempTable.findIndex(item => item.id === id);
            const selectedRow = tempTable[index];
            selectedRow.loading = true;
            tempTable[index] = selectedRow;
            setTableData(tempTable);

            axiosInstance.post('/catalogue/projectCreator/fileupload', formData).then(({ data: response }) => {
                let data = {
                    ...selectedRow,
                    paymentFilePath: response[0].filePath,
                };
                axiosInstance
                    .put('catalogue/projectCreator/updateInvoice', {
                        ...data,
                    })
                    .then(() => {
                        selectedRow.paymentFilePath = response[0].filePath;
                        selectedRow.loading = false;
                        tempTable[index] = selectedRow;

                        setTableData(tempTable);
                    });
            });
        }
    };

    React.useEffect(() => {
        uploadingProgress &&
            uploadingProgress.map(item => {
                if (item.loading) {
                    console.log(item);
                }
                return item;
            });
    }, [uploadingProgress]);

    const handleStatement = (filepath: string, id: number, loading: any) => {
        if (loading) {
            return <CircularProgress />;
        }
        if (filepath) {
            return (
                <div className={css.fileuploadsection}>
                    <Button component="label" style={{ color: '#8eb46d' }}>
                        <CloudDoneOutlined />
                        UPLOADED
                        <input
                            hidden
                            type="file"
                            name="statement"
                            id={`statement_${id}`}
                            onChange={e => {
                                handleFileUpload(e, id);
                            }}
                        />
                    </Button>
                    <Button component="label" style={{ color: '#bababa' }}>
                        <VisibilityOutlinedIcon color={'inherit'} className={classNames(css.colorYellow)} style={{ color: '#bababa' }} />
                        view
                    </Button>
                    <Button component="label" style={{ color: '#bababa' }}>
                        <GetApp color={'inherit'} className={classNames(css.colorYellow)} style={{ color: '#bababa' }} />
                        Download
                    </Button>
                    {/*<a target="_blank" href={filepath} rel="noopener noreferrer">*/}
                    {/*    <div>*/}
                    {/*        <VisibilityOutlinedIcon color={'inherit'} className={classNames(css.colorYellow)} style={{ color: '#bababa' }} />*/}
                    {/*        View*/}
                    {/*    </div>*/}
                    {/*</a>*/}
                </div>
            );
        } else {
            return (
                <div className={css.fileuploadsection}>
                    <Button component="label">
                        <CloudUploadOutlined />
                        UPLOAD
                        <input
                            hidden
                            type="file"
                            name="statement"
                            id={`statement_${id}`}
                            onChange={e => {
                                handleFileUpload(e, id);
                            }}
                        />
                    </Button>
                </div>
            );
        }
    };

    const projectId = match.params.creatorId;

    const dealDetailsRoute = useMemo(() => `/creators-list/${projectId}/dashboard/deals/table/${match.params.dealId}/details`, [ // eslint-disable-line
        match.url,
        match.params.dealId,
    ]);

    const goToDealDetails = useCallback(() => route(dealDetailsRoute), [dealDetailsRoute, route]);
    return isEdiTable ? (
        <div className={css.module}>
            <form className={css.form} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={6} style={{ display: 'none' }}>
                        <TextField
                            className={css.input}
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="id"
                            type="number"
                            defaultValue={editRowData.id}
                            inputRef={register}
                            // helperText={errors.dealName ? errors.dealName.message : ''}
                        />
                        <TextField
                            className={css.input}
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="paymentMethod"
                            type="text"
                            defaultValue={editRowData.paymentMethod}
                            inputRef={register}
                            // helperText={errors.dealName ? errors.dealName.message : ''}
                        />
                        <TextField
                            className={css.input}
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="paymentFilePath"
                            type="text"
                            defaultValue={editRowData.paymentFilePath}
                            inputRef={register}
                            // helperText={errors.dealName ? errors.dealName.message : ''}
                        />
                        <TextField variant="filled" fullWidth autoFocus name="currency" defaultValue={editRowData.currency || 'USD'} inputRef={register} />
                        <TextField
                            className={css.input}
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="isPaid"
                            type="text"
                            defaultValue={editRowData.isPaid}
                            inputRef={register}
                            // helperText={errors.dealName ? errors.dealName.message : ''}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl variant="filled" className={css.input} style={{ width: '100%' }}>
                            <InputLabel id="demo-simple-select-filled-label">Payment Method</InputLabel>
                            <Select
                                labelId="demo-simple-select-filled-label"
                                id="demo-simple-select-filled"
                                // value={editRowData.paymentMethod}
                                defaultValue={editRowData.paymentMethod}
                                onChange={(e: any) => setValue('paymentMethod', e.target.value)}
                                style={{ width: '100%' }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                <MenuItem value={0}>Monthly</MenuItem>
                                <MenuItem value={1}>Quarterly</MenuItem>
                                <MenuItem value={2}>Bi-Annual</MenuItem>
                                <MenuItem value={3}>Yearly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="period-cover"
                            label="Period it Covers	"
                            type="date"
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="periodItCovers"
                            defaultValue={moment(editRowData.periodItCovers).format('yyyy-MM-DD')}
                            inputRef={register}
                            // className={classes.textField}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="due-date"
                            label="Due By"
                            type="date"
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="paymentDate"
                            defaultValue={moment(editRowData.paymentDate).format('yyyy-MM-DD')}
                            inputRef={register}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            id="received-date"
                            label="Received"
                            type="date"
                            variant="filled"
                            fullWidth
                            autoFocus
                            defaultValue={moment(editRowData.paymentReceivedDate).format('yyyy-MM-DD')}
                            name="paymentReceivedDate"
                            inputRef={register({ required: true })}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            id="amount"
                            label="Amount"
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="amount"
                            defaultValue={editRowData.amount}
                            inputRef={register({ required: true })}
                        />
                    </Grid>

                    {/* <Grid item xs={6}>
                        <TextField
                            id="currency"
                            label="Currency"
                            variant="filled"
                            fullWidth
                            autoFocus
                            name="currency"
                            defaultValue={editRowData.currency}
                            inputRef={register({ required: true })}
                        />
                    </Grid> */}

                    <Grid item xs={6}>
                        <FormControl variant="filled" className={css.input} style={{ width: '100%' }}>
                            <InputLabel id="currencyLabel"> Currency </InputLabel>
                            <Select
                                labelId="currencyLabel"
                                id="currencyl"
                                // value={editRowData.paymentMethod}
                                name="currency"
                                defaultValue={editRowData.currency || 'USD'}
                                onChange={(e: any) => setValue('currency', e.target.value)}
                                fullWidth
                                style={{ width: '100%' }}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                <MenuItem value={'USD'}>USD</MenuItem>
                                <MenuItem value={'RUPEE'}>RUPEE</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl variant="filled" className={css.input}>
                            <InputLabel id="paid"> Paid?</InputLabel>
                            <Select
                                labelId="paid"
                                id="paid"
                                // value={editRowData.paymentMethod}
                                name="isPaid"
                                defaultValue={editRowData.isPaid}
                                onChange={(e: any) => setValue('isPaid', e.target.value)}
                                fullWidth
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                <MenuItem value={0}>NO</MenuItem>
                                <MenuItem value={1}>YES</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Button type="submit">SAVE</Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    ) : (
        <div className={css.module}>
            <Box className={css.header}>
                <Avatar src={''} style={{ marginRight: '10px' }} />
                <Typography align={'left'} variant={'h5'} className={'bold'}>
                    {`${dealTitle.toUpperCase()}\'S ENTRY`}
                </Typography>
                <Button className={css.viewDeal} onClick={() => goToDealDetails()} style={{ float: 'right' }}>
                    View Deal
                </Button>
            </Box>
            <div>
                <TableContainer component={Paper}>
                    <Table className={''} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Payment method</StyledTableCell>
                                <StyledTableCell align="center">Period it Covers</StyledTableCell>
                                <StyledTableCell align="center">Due By</StyledTableCell>
                                <StyledTableCell align="center">Received</StyledTableCell>
                                <StyledTableCell align="center">Amount</StyledTableCell>
                                <StyledTableCell align="center">Currency</StyledTableCell>
                                <StyledTableCell align="center">Paid</StyledTableCell>
                                <StyledTableCell align="left">Statement</StyledTableCell>
                                <StyledTableCell align="center">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody style={{ position: 'relative' }}>
                            {!isLoading ? (
                                tableData && tableData.length >= 0 ? (
                                    tableData.length === 0 ? (
                                        'No Records found'
                                    ) : (
                                        tableData.map((t, i) => {
                                            return (
                                                <TableRow key={t.id}>
                                                    <StyledTableCell component="th" scope="row">
                                                        {t.paymentMethod === 0
                                                            ? 'Monthly'
                                                            : t.paymentMethod === 1
                                                            ? 'Quarterly'
                                                            : t.paymentMethod === 2
                                                            ? 'Bi-Annual'
                                                            : 'Yearly'}
                                                    </StyledTableCell>
                                                    <StyledTableCell align="center">
                                                        <DateFormat newDate={t.periodItCovers} />
                                                    </StyledTableCell>
                                                    <StyledTableCell align="center">
                                                        <DateFormat newDate={t.paymentDate} />
                                                    </StyledTableCell>
                                                    <StyledTableCell align="center">
                                                        {t.paymentReceivedDate && <DateFormat newDate={t.paymentReceivedDate} />}
                                                    </StyledTableCell>
                                                    <StyledTableCell align="center">{t.amount}</StyledTableCell>
                                                    <StyledTableCell align="center">{t.currency}</StyledTableCell>
                                                    <StyledTableCell align="center" className={t.isPaid === 0 ? 'notPaid' : 'ifPaid'}>
                                                        {t.isPaid === 0 ? (
                                                            <HighlightOffOutlined style={{ color: 'red' }} />
                                                        ) : (
                                                            <CheckCircleOutlineRounded style={{ color: '#8eb46d' }} />
                                                        )}
                                                    </StyledTableCell>
                                                    <StyledTableCell align="left">{handleStatement(t.paymentFilePath, t.id, t.loading)}</StyledTableCell>
                                                    <StyledTableCell align="center">
                                                        <VerticalPopver
                                                            childClassName={css.editIcon}
                                                            actionsList={`edit,delete`}
                                                            onClickEdit={() => editTableData(t.id)}
                                                            onClickDelete={() => deleteTableData(t.id)}
                                                        />
                                                    </StyledTableCell>
                                                </TableRow>
                                            );
                                        })
                                    )
                                ) : (
                                    // <CircularProgress style={{ position: 'absolute', left: '55%' }} />
                                    'No Recordes found'
                                )
                            ) : (
                                <CircularProgress style={{ position: 'absolute', left: '55%' }} />
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            {tableData.length > 0 && (
                <Box className={css.paginationWrapper}>
                    <Typography className={css.total}>Total: {total}</Typography>
                    <Pagination
                        className={css.pagination}
                        count={pagesCount}
                        page={currentPage}
                        onChange={(event: any, value: any) => {
                            setCurrentPage(value);
                        }}
                        variant="outlined"
                        shape="rounded"
                        hideNextButton
                        hidePrevButton
                    />
                </Box>
            )}
            {/*<div className={css.pagination}>*/}
            {/*    {tableData.length > 0 && (*/}
            {/*        <Button*/}
            {/*            onClick={() => setOffset(offset - 10)}*/}
            {/*            disabled={offset === 0}*/}
            {/*            color="primary"*/}
            {/*            variant="contained"*/}
            {/*            style={{ background: '#6CB9A4' }}*/}
            {/*        >*/}
            {/*            Previous*/}
            {/*        </Button>*/}
            {/*    )}*/}
            {/*    {Array.from(range(0, totalCount, limit)).map((item: number, i: any) => {*/}
            {/*        return (*/}
            {/*            <div*/}
            {/*                key={i}*/}
            {/*                onClick={() => setOffset(item)}*/}
            {/*                className={css.pageno}*/}
            {/*                style={{ background: item === offset ? 'rgb(238, 122, 82)' : '#8eb46d' }}*/}
            {/*            >*/}
            {/*                {i + 1}*/}
            {/*            </div>*/}
            {/*        );*/}
            {/*    })}*/}
            {/*    {tableData.length > 0 && (*/}
            {/*        <Button*/}
            {/*            onClick={() => setOffset(offset + 10)}*/}
            {/*            color="primary"*/}
            {/*            disabled={Array.from(range(0, totalCount, limit))[Array.from(range(0, totalCount, limit)).length - 1] === offset}*/}
            {/*            variant="contained"*/}
            {/*            style={{ background: '#6CB9A4' }}*/}
            {/*        >*/}
            {/*            Next*/}
            {/*        </Button>*/}
            {/*    )}*/}
            {/*</div>*/}
        </div>
    );
};
